import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4"></i>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">未找到该文章</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleUrl = `${siteInfo.url}/article/${slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: article.category, item: `${siteInfo.url}/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={articleUrl}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />

      <main>
        <Navbar />

        <article className="pt-20 pb-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <header className="mt-6 mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-block px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 whitespace-nowrap hover:bg-primary-200 transition-colors"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.date}</span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none mb-12
                prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-sm md:prose-p:text-base prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-950
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-semibold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_p]:text-sm [&_p]:md:text-base [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4
                [&_strong]:text-foreground-950"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-12 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200/70 pt-10">
                <h2 className="text-lg md:text-xl font-semibold text-foreground-950 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-background-300/60 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <Footer />
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}